import React, { useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import {
  AdminAuthProvider,
  useAdminAuth,
  can,
} from "../../lib/admin-auth-context";
import { LOGO_URL } from "../../lib/i18n";
import {
  House,
  ChatCircle,
  Users,
  Package,
  Receipt,
  Truck,
  CurrencyDollar,
  UserGear,
  UserCircle,
  SignOut,
  List as ListIcon,
  X,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: House, perm: "ops" },
  { to: "/admin/quotes", label: "Cotizaciones", icon: ChatCircle, perm: "ops" },
  { to: "/admin/clients", label: "Clientes", icon: Users, perm: "ops" },
  { to: "/admin/inventory", label: "Inventario", icon: Package, perm: "ops" },
  { to: "/admin/orders", label: "Pedidos", icon: Receipt, perm: "ops" },
  { to: "/admin/shipments", label: "Envíos", icon: Truck, perm: "ops" },
  { to: "/admin/invoices", label: "Facturas", icon: CurrencyDollar, perm: "finance" },
  { to: "/admin/portal-users", label: "Portal Clientes", icon: UserCircle, perm: "finance" },
  { to: "/admin/users", label: "Usuarios", icon: UserGear, perm: "users" },
];

const Sidebar = ({ user, onClose }) => {
  const { logout } = useAdminAuth();
  const nav = useNavigate();
  return (
    <aside
      className="w-64 bg-black text-white flex flex-col h-full border-r border-white/10"
      data-testid="admin-sidebar"
    >
      <div className="px-5 h-20 flex items-center justify-between border-b border-white/10">
        <Link to="/admin" className="flex items-center gap-2">
          <img
            src={LOGO_URL}
            alt="ICOR"
            className="h-12 w-auto"
            style={{ mixBlendMode: "screen", filter: "brightness(2.6) contrast(1.1)" }}
          />
        </Link>
        {onClose && (
          <button onClick={onClose} className="lg:hidden text-white/70" aria-label="Close">
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="flex-1 py-5 space-y-1 px-3 overflow-y-auto">
        {NAV.filter((n) => can(user.role, n.perm)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-all ${
                isActive
                  ? "bg-[var(--icor-blue)] text-white"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <n.icon size={18} weight="duotone" />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/10">
        <div className="px-3 py-2 mb-2">
          <div className="text-sm font-medium tracking-tight">{user.name}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-0.5">
            {user.role}
          </div>
        </div>
        <button
          onClick={async () => {
            await logout();
            nav("/admin/login", { replace: true });
          }}
          data-testid="admin-logout"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-sm"
        >
          <SignOut size={16} />
          Cerrar sesión
        </button>
      </div>
    </aside>
  );
};

const Shell = () => {
  const { user } = useAdminAuth();
  const [open, setOpen] = useState(false);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="admin-shell">
      {/* Desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">
        <Sidebar user={user} />
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setOpen(false)}
          />
          <div className="relative h-full">
            <Sidebar user={user} onClose={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden h-14 bg-black text-white flex items-center justify-between px-4 border-b border-white/10">
          <button onClick={() => setOpen(true)} aria-label="Menu">
            <ListIcon size={22} />
          </button>
          <span className="font-display text-sm tracking-tight">ICOR Admin</span>
          <span />
        </header>
        <main className="flex-1 p-6 lg:p-10 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  return (
    <AdminAuthProvider>
      <Shell />
      <Toaster position="top-right" richColors />
    </AdminAuthProvider>
  );
}
